import React, { useEffect, useMemo, useState } from "react";
import api, { API } from "../lib/api";
import { EmptyState } from "../components/EmptyState";
import CostCodeDetail from "../components/CostCodeDetail";
import { AdjustedCell } from "../components/AdjustedCell";
import { useAuth } from "../context/AuthContext";
import { nf, pf, pct, signed, money } from "../lib/format";
import { statusMeta, pfColor, gainColor } from "../lib/status";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Loader2, Search, Layers, ChevronsDownUp, ChevronsUpDown, Download, ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

// Same columns/order as the per-project Productivity tab (read-only here).
const COLS = [
  { k: "cost_code", label: "Cost Code", w: "w-28", fmt: (v) => v },
  { k: "description", label: "Description", w: "min-w-[150px]", fmt: (v) => v },
  { k: "uom", label: "UOM", fmt: (v) => v },
  { k: "standard_labor_rate", label: "Std $/hr", num: true, fmt: (v) => (v ? `$${nf(v, 0)}` : "—") },
  { k: "original_qty", label: "Orig Qty", num: true, g: true, qty: true, fmt: (v) => nf(v, 0) },
  { k: "change_qty", label: "Chg Qty", num: true, qty: true, fmt: (v) => nf(v, 0) },
  { k: "budget_adj_qty", label: "Budget Adj Qty", num: true, qty: true, editable: true, fmt: (v) => nf(v, 0) },
  { k: "revised_qty", label: "Rev Qty", num: true, qty: true, fmt: (v) => nf(v, 0) },
  { k: "original_hours", label: "Orig Hrs", num: true, g: true, fmt: (v) => nf(v) },
  { k: "change_hours", label: "Chg Hrs", num: true, fmt: (v) => nf(v) },
  { k: "revised_hours", label: "Rev Hrs", num: true, fmt: (v) => nf(v) },
  { k: "original_unit_rate", label: "Orig U/hr", num: true, g: true, qty: true, fmt: (v) => nf(v, 2) },
  { k: "revised_unit_rate", label: "Rev U/Hr", num: true, qty: true, fmt: (v) => nf(v, 2) },
  { k: "actual_unit_rate", label: "Actual U/hr", num: true, qty: true, fmt: (v) => nf(v, 2) },
  { k: "installed_qty", label: "Installed Qty", num: true, g: true, qty: true, fmt: (v) => nf(v, 0) },
  { k: "sage_hours", label: "Sage hrs", num: true, fmt: (v) => nf(v) },
  { k: "adjusted_hours", label: "Adjusted hrs", num: true, fmt: (v) => nf(v) },
  { k: "actual_hours", label: "Actual Hrs", num: true, fmt: (v) => nf(v) },
  { k: "remaining_qty", label: "Remain Qty", num: true, g: true, qty: true, fmt: (v) => nf(v, 0) },
  { k: "remaining_hours", label: "Remain Hrs", num: true, fmt: (v) => nf(v) },
  { k: "percent_complete", label: "% Comp", num: true, qty: true, fmt: (v) => pct(v) },
  { k: "earned_hours", label: "Earned Hrs", num: true, fmt: (v) => nf(v) },
  { k: "productivity_factor", label: "PF", num: true, pfc: true, fmt: (v) => pf(v) },
  { k: "forecast_hours", label: "Fcst Hrs", num: true, g: true, fmt: (v) => nf(v) },
  { k: "forecast_gain_loss", label: "Fcst hrs +/-", num: true, gain: true, fmt: (v) => signed(v) },
  { k: "forecast_dollar_gain_loss", label: "Fcst $+/-", num: true, gain: true, fmt: (v) => money(v) },
  { k: "jtd_hours_gain_loss", label: "JTD Hrs +/-", num: true, gain: true, g: true, fmt: (v) => signed(v) },
  { k: "jtd_dollar_gain_loss", label: "JTD $ +/-", num: true, gain: true, fmt: (v) => money(v) },
  { k: "status", label: "Status", g: true, fmt: (v) => v },
];

const TOTAL_MAP = {
  original_qty: "total_original_qty", change_qty: "total_change_qty", budget_adj_qty: "total_budget_adj_qty", revised_qty: "total_revised_qty",
  original_hours: "total_original_hours", change_hours: "total_change_hours", revised_hours: "total_revised_hours",
  original_unit_rate: "total_original_unit_rate", revised_unit_rate: "total_revised_unit_rate",
  actual_unit_rate: "total_actual_unit_rate", installed_qty: "total_installed_qty",
  sage_hours: "total_sage_hours", adjusted_hours: "total_adjusted_hours", actual_hours: "total_actual_hours",
  remaining_qty: "total_remaining_qty", remaining_hours: "total_remaining_hours",
  percent_complete: "total_percent_complete", earned_hours: "total_earned_hours",
  productivity_factor: "overall_productivity_factor", forecast_hours: "total_forecast_hours",
  forecast_gain_loss: "total_forecast_gain_loss", forecast_dollar_gain_loss: "total_forecast_dollar_gain_loss",
  jtd_hours_gain_loss: "jtd_hours_gain_loss", jtd_dollar_gain_loss: "jtd_dollar_gain_loss",
};

function TotalsRow({ label, totals, grand, sticky, onClickLabel }) {
  const bg = grand ? "bg-[#0B192C]" : "bg-[#1E3E62]";
  const txt = grand ? "text-white font-semibold" : "text-white font-medium";
  const border = "border-[#3a5474]";
  return (
    <tr className={`${bg} ${txt} ${sticky ? "sticky bottom-0" : ""}`}
        data-testid={grand ? "exec-grand-total-row" : undefined}>
      <td className={`px-2 py-2 whitespace-nowrap ${bg} ${grand ? "" : "cursor-pointer hover:underline"}`}
          onClick={onClickLabel}>{label}</td>
      {COLS.map((c) => {
        if (c.k === "cost_code") return <td key={c.k} className={`px-2 py-2 text-slate-300 text-[11px] ${bg}`}>roll-up</td>;
        const key = TOTAL_MAP[c.k];
        if (!key || (grand && c.qty)) return <td key={c.k} className={`${bg} ${c.g ? "border-l-2 " + border : ""}`}></td>;
        const v = totals[key];
        return (
          <td key={c.k} className={`px-2 py-2 text-right font-mono ${bg} ${c.g ? "border-l-2 " + border : ""}`}>
            {c.fmt(v)}
          </td>
        );
      })}
    </tr>
  );
}

export default function ExecutiveProductivity() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [projFilter, setProjFilter] = useState("all");
  const [rolledUp, setRolledUp] = useState(false);
  const [detail, setDetail] = useState(null);
  const { can } = useAuth();
  const load = () => api.get("/productivity/executive").then((r) => setData(r.data));

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, []);

  const groups = useMemo(() => {
    if (!data) return [];
    let g = data.projects;
    if (projFilter !== "all") g = g.filter((p) => p.id === projFilter);
    return g.map((p) => ({
      ...p,
      filteredRows: q
        ? p.rows.filter((r) => `${r.cost_code} ${r.description}`.toLowerCase().includes(q.toLowerCase()))
        : p.rows,
    }));
  }, [data, projFilter, q]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0B192C]" /></div>;
  if (!data || data.project_count === 0)
    return <EmptyState title="No projects yet" hint="Create a project to roll up productivity across the company." />;

  return (
    <div className="space-y-4" data-testid="executive-productivity-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-[#0B192C] flex items-center justify-center">
            <Layers className="w-6 h-6 text-[#E8791A]" />
          </div>
          <div>
            <h1 className="font-head text-2xl font-bold text-slate-900">Executive Productivity Roll-Up</h1>
            <p className="text-sm text-slate-500">
              {data.project_count} project{data.project_count === 1 ? "" : "s"} · {data.cost_code_count} cost codes ·
              company-wide grand roll-up at the bottom
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setRolledUp((v) => !v)} data-testid="roll-up-toggle"
                  className="bg-[#E8791A] hover:bg-[#D66A0F] text-white">
            {rolledUp ? <><ChevronsUpDown className="w-4 h-4 mr-2" />Expand Line Items</>
                      : <><ChevronsDownUp className="w-4 h-4 mr-2" />Roll Up</>}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="exec-prod-export" className="hover:bg-slate-900 hover:text-white">
                <Download className="w-4 h-4 mr-2" />Export Excel<ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem data-testid="exec-prod-export-expanded"
                                onSelect={() => window.open(`${API}/productivity/executive/export.xlsx?mode=expanded`, "_blank")}>
                Expanded — every cost-code line
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="exec-prod-export-rollup"
                                onSelect={() => window.open(`${API}/productivity/executive/export.xlsx?mode=rollup`, "_blank")}>
                Roll-up — project subtotals only
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input placeholder="Search cost code…" value={q} onChange={(e) => setQ(e.target.value)}
                 className="pl-8 w-56 h-9" data-testid="exec-prod-search" />
        </div>
        <Select value={projFilter} onValueChange={setProjFilter}>
          <SelectTrigger className="h-9 w-64 text-sm" data-testid="exec-prod-project-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            {data.projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.project_number} — {p.project_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500 ml-auto">
          {rolledUp ? "Rolled up — showing project subtotals only" : "Expanded — showing every cost-code line"}
        </span>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm">
        <div className="report-scroll">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-slate-100">
                <th className="px-2 py-2 text-left text-slate-600 font-semibold uppercase tracking-wide whitespace-nowrap">Project</th>
                {COLS.map((c) => (
                  <th key={c.k}
                      className={`px-2 py-2 text-slate-600 font-semibold uppercase tracking-wide whitespace-nowrap 
                        ${c.num ? "text-right" : "text-left"} ${c.g ? "border-l-2 border-slate-300" : ""} ${c.w || ""}`}
                      data-testid={`exec-col-${c.k}`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((grp) => (
                <React.Fragment key={grp.id}>
                  {!rolledUp && grp.filteredRows.map((r, ri) => {
                    const meta = statusMeta(r.status);
                    return (
                      <tr key={grp.id + r.cost_code}
                          onClick={() => setDetail({ projectId: grp.id, code: r.cost_code })}
                          className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${meta.row}`}
                          data-testid={`exec-prod-row-${grp.project_number}-${r.cost_code}`}>
                        <td className="px-2 py-1.5 whitespace-nowrap font-mono text-[11px] text-slate-500">
                          {ri === 0 ? grp.project_number : ""}
                        </td>
                        {COLS.map((c) => {
                          let cls = "px-2 py-1.5 whitespace-nowrap ";
                          cls += c.num ? "text-right font-mono " : "text-left ";
                          if (c.pfc) cls += `font-semibold ${pfColor(r[c.k])} `;
                          if (c.gain) cls += gainColor(r[c.k]) + " ";
                          if (c.g) cls += "border-l-2 border-slate-200 ";
                          if (c.k === "status") {
                            return (
                              <td key={c.k} className="px-2 py-1.5 border-l-2 border-slate-200">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${meta.badge}`}>
                                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.dot }} />
                                  {r.status}
                                </span>
                              </td>
                            );
                          }
                          if (c.editable || (r.cost_type === "OT" && c.k === "standard_labor_rate")) {
                            return (
                              <td key={c.k} className={cls}>
                                <AdjustedCell row={r} pid={grp.id} field={c.k} canEdit={can.admin} onSaved={load} />
                              </td>
                            );
                          }
                          return <td key={c.k} className={cls}>{c.fmt(r[c.k])}</td>;
                        })}
                      </tr>
                    );
                  })}
                  <TotalsRow
                    label={`${grp.project_number} — ${grp.project_name}`}
                    totals={grp.totals}
                    onClickLabel={() => setProjFilter(projFilter === grp.id ? "all" : grp.id)}
                  />
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <TotalsRow label="EXECUTIVE ROLL-UP (ALL PROJECTS)" totals={data.grand_totals} grand sticky />
            </tfoot>
          </table>
        </div>
      </div>
      <p className="text-[11px] text-slate-400">
        The grand roll-up sums labor hours, earned hours, forecast and gain/loss across all projects (overall PF = total earned ÷ total actual).
        Quantities and unit rates are intentionally blank on the grand row because they mix units of measure (LF, SF, EA, …) across projects.
      </p>

      {detail && (
        <CostCodeDetail projectId={detail.projectId} code={detail.code} onClose={() => setDetail(null)} />
      )}
    </div>
  );
}
